<script lang="ts" context="module">
	/**
	 * Hack to avoid relying on server to provide up-to-date saved items list immediately after 
	 * an item un/save action: adjust the list that the server sends to us to include/exclude
	 * an item that has just been un/saved.
	 * @param ssList The saved items list that we got from the server
	 * @param updatedProduct The product that has just been un/saved
	 * @returns `ssList` modified to include/exclude `updatedProduct`
	 */
	// TODO: If you un/save a couple of items in quick succession, this doesn't work as it
	// assumes just the latest update could be out of sync.
	export const manuallyUpdateSavedList = (
		ssList: ProductData[],
		updatedProduct: ProductData,
	) => {
		const updatedProductIdx = ssList.findIndex(
			(product) => product.catalogue_key === updatedProduct.catalogue_key);
		const updatedProductPresentInSavedList = updatedProductIdx >= 0;

		if (updatedProduct.user_specific?.saved_list // product was just saved
			&& !updatedProductPresentInSavedList) {
			ssList.unshift(updatedProduct);
		} else if (!updatedProduct.user_specific?.saved_list // product was just un-saved
			&& updatedProductPresentInSavedList) {
				ssList.splice(updatedProductIdx, 1);
		}

		return ssList;
	};

	const getGridViewUrl = (pageUrl: Page["url"]) => "/?" + query(pageUrl)
				.set(BUY_AGAIN, SAVED_LIST_FILTER_ID)
				.remove(KEY_AISLE);

	export const processDataForSideNavUpdate = (
		data: ValueFromArray<StoreLanding["layout"]>,
	): SideNavMainItem => {return {
			id: data.type,
			title: data.title,
			icon: 'icon-heart-20',
			setHref: (page, item) => getGridViewUrl(page.url),
			active: (page, item) => {
				const q = query(page.url)
				return q.hasValue(BUY_AGAIN, SAVED_LIST_FILTER_ID);
			},
		};
	};
</script>

<script lang="ts">
	import type { ProductData, StoreLanding } from "$lib/def/landing";
	import type { Cache } from "$lib/serv/core/cache";
	import { getSavedStatusUpdates } from "$lib/serv/groc-product";
	import type { SideNavMainItem } from "$lib/serv/nav";
	import { fetchAisleInfo } from "$lib/ui/home/groc/swimlanes/BuyAgain.svelte";
	import { BUY_AGAIN, SAVED_LIST_FILTER_ID } from "$lib/ui/home/groc/UserSpecificAisle.svelte";
	import UserSpecificSwimlane from "$lib/ui/home/groc/UserSpecificSwimlane.svelte";
	import { query } from "$lib/util/url";
	import type { Page } from "@sveltejs/kit";
	import {
	    BehaviorSubject,
	    catchError, concat, concatMap,
	    merge,
	    of, switchMap, type Observable,
	    type ValueFromArray
	} from "rxjs";
	import { KEY_AISLE } from "../Aisle.svelte";
	
let refresh$ = new BehaviorSubject<boolean>(false);
const cache = merge(
		getSavedStatusUpdates(),
		refresh$,
	).pipe(
		concatMap<
			ProductData | boolean,
			Observable<Cache<{ title: string, products: ProductData[]}>>
		>((update) => fetchAisleInfo(data, store).pipe(
			// Example of error is `fillLayout` not finding any of the requested items in this store
			catchError((fetchError) => of({
				loading: false,
				payload: {
					title: data.title,
					products: [],
				},
			})),
			switchMap((aisle) => {
				const requestToRefresh = typeof update === "boolean";
				if (requestToRefresh || aisle.loading) return of(aisle);

				// If the aisle is done loading and there is a product that just got un/saved, we might
				// need to double-check the product list (server updates happen asynchronously so what we
				// get back might be out-of-date data).

				return concat(
					of({ loading: true }),  // In case manually updating the saved list takes a bit of time
					of({
						loading: false,
						payload: {
							title: aisle.payload?.title ?? data.title,
							products: manuallyUpdateSavedList(aisle.payload?.products ?? [], update),
						},
					}),
				);
			}),
		)),
	);

	export let data: ValueFromArray<StoreLanding["layout"]>;
	export let page: Pick<Page, "url">;

	/** Store id of the store user is browsing currently */
	let store: string;
	
	$: {
		[,store,,] = data?.endpoint.split('/'); // "/[store_code]/buy-again..."
		// i.e. refresh whenever `data` changes
		refresh$.next(true);
	}

	$: gridViewUrl = getGridViewUrl(page.url);

</script>

<UserSpecificSwimlane
	cache={$cache}
	title={$cache?.payload?.title || ''}
	{gridViewUrl}
	on:product
/>